import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';

export default function QrScanner({ onScan }) {
  const SCANNER_ID = 'qr-reader-pro';
  const html5QrCodeRef = useRef(null);

  const [scannedId, setScannedId] = useState(null);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getQrboxSize = () => {
    const width = Math.min(window.innerWidth, 360);
    return Math.floor(width * 0.75);
  };

  const clearScannerDom = () => {
    const wrapper = document.getElementById('scanner-wrapper');
    if (wrapper) {
      wrapper.innerHTML = `<div id="${SCANNER_ID}"></div>`;
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.warn('Error stopping scanner:', err.message);
      } finally {
        html5QrCodeRef.current = null;
        setIsRunning(false);
      }
    }
  };

  const startScanner = async () => {
    setIsLoading(true);
    await stopScanner();
    clearScannerDom();

    const scanner = new Html5Qrcode(SCANNER_ID);
    html5QrCodeRef.current = scanner;

    try {
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: getQrboxSize(), height: getQrboxSize() } },
        (decodedText) => {
          try {
            const url = new URL(decodedText);
            const id = url.searchParams.get('id');
            if (id) {
              setScannedId(id);
              onScan(decodedText);
              stopScanner();
            } else {
              setError('Missing `id` in QR code.');
            }
          } catch {
            setError('Invalid QR code format.');
          }
        },
        () => {}
      );
      setIsRunning(true);
    } catch (err) {
      setError('Failed to start scanner: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRescan = async () => {
    setScannedId(null);
    setError(null);
    await startScanner();
  };

  useEffect(() => {
    clearScannerDom();
    startScanner();

    return () => {
      stopScanner();
    };
  }, []);

  return (
    <Box sx={{ padding: 4 }}>
      {!isRunning && !scannedId && (
        <Box display='flex' justifyContent='center' mt={2}>
          <CircularProgress />
        </Box>
      )}

      {!scannedId && (
        <Box
          id='scanner-wrapper'
          sx={{
            width: '100%',
            maxWidth: 360,
            margin: 'auto',
          }}
        >
          <div id={SCANNER_ID}></div>
        </Box>
      )}

      {scannedId && (
        <Box mt={2}>
          <Typography variant='body1'>
            Scanned ID: <strong>{scannedId}</strong>
          </Typography>
          <Button variant='outlined' onClick={handleRescan} sx={{ mt: 1 }}>
            Rescan
          </Button>
        </Box>
      )}

      {error && (
        <Typography color='error' mt={2}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
